import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';

import { ListItem } from '@yandex-360/orb-components/dist/components/ListItem/bundle';
import { useSelector } from 'react-redux';

import { i18n } from '../../../../../../../helpers/i18n';
import { ACTION, FEATURE } from '../../../../../../../helpers/logger-enums';
import { createLogger } from '../../../../../../../helpers/loggerFacade';
import { useActions } from '../../../../../../../hooks';
import { useTypedSelector } from '../../../../../../../hooks/useTypedSelector';
import {
    getDevicesByTypeSelector,
    getVideoFacingMode,
    getVideoInputDeviceSelector,
    setVideoInputDeviceAction,
    toggleCameraFacingModeAction,
} from '../../../../../../settings';
import { FacingMode } from '../../../../../../settings/types';
import { isMobileUa } from '../../../../../../uatraits';
import { Island } from '../../../island';
import { SettingsSelect } from '../../../settings-select';
import { VideoPreview } from '../../../video-preview';
import { VideoControls } from '../video-controls';
import css from './styles.css';

const logger = createLogger('settings-modal-v2-video');

export const VideoSupported: FunctionComponent = () => {
    const devices = useTypedSelector((state) => getDevicesByTypeSelector(state, 'videoinput'));

    const { toggleCameraFacingMode, setVideoInputDevice } = useActions({
        toggleCameraFacingMode: toggleCameraFacingModeAction,
        setVideoInputDevice: setVideoInputDeviceAction,
    });

    const isMobile = useSelector(isMobileUa);
    const currentDeviceId = useTypedSelector((state) => getVideoInputDeviceSelector(state)?.deviceId);
    const facingMode = useSelector(getVideoFacingMode);

    const deviceIdOrFacingMode = isMobile ? facingMode : currentDeviceId;

    const items = useMemo(() => {
        if (isMobile) {
            return [
                { itemKey: FacingMode.USER, label: i18n('settings-v2', 'camera-option-front') },
                { itemKey: FacingMode.ENVIRONMENT, label: i18n('settings-v2', 'camera-option-back') },
            ];
        }
        return devices.map(({ deviceId, label }) => ({
            itemKey: deviceId,
            label,
        }));
    }, [devices, isMobile]);

    /** Используем состояние селекта на локальном стейте,
     *  т.к. через саги камера переключается не мгновенно, из-за этого в store новое значение приходит довольно долго */
    const [desiredDeviceIdOrFacingMode, setDesiredDeviceIdOrFacingMode] = useState<FacingMode | Maybe<string>>(
        deviceIdOrFacingMode
    );

    const setDevice = useCallback(
        (itemKey: React.Key | null) => {
            if (itemKey) {
                const idOrModeValue = itemKey as string;
                if (idOrModeValue === desiredDeviceIdOrFacingMode) {
                    /** Если выбрали то же самое, то ничего не делаем, чтоб лишний раз спинер не крутить */
                    return;
                }

                if (isMobile) {
                    toggleCameraFacingMode(idOrModeValue as FacingMode);
                } else {
                    logger.startFeatureDelta({
                        feature: FEATURE.DEVICES,
                        action: ACTION.SELECT_CAMERA,
                    });
                    setVideoInputDevice(idOrModeValue);
                }
                setDesiredDeviceIdOrFacingMode(idOrModeValue);
            }
        },
        [desiredDeviceIdOrFacingMode, isMobile, toggleCameraFacingMode, setVideoInputDevice]
    );

    const cameraSelect = (
        <SettingsSelect
            aria-label={i18n('settings-v2', 'video-camera')}
            items={items}
            selectedKey={desiredDeviceIdOrFacingMode}
            onSelectionChange={setDevice}
        />
    );

    return (
        <Island className={css.videoSupportedWrapper}>
            <div className={css.preview}>
                <VideoPreview deviceIdOrFacingMode={desiredDeviceIdOrFacingMode} />
            </div>
            <div className={css.controlsWrapper}>
                <div className={css.controls}>
                    <VideoControls>
                        <ListItem
                            label={i18n('settings-v2', 'video-camera')}
                            endSlot={cameraSelect}
                            interaction={false}
                        />
                    </VideoControls>
                </div>
            </div>
        </Island>
    );
};
